import moment from 'moment';
import {
  generatePdfFromTemplate,
  PdfTemplateLayoutItem,
  GeneratePdfTemplateResult,
  PdfTemplateFontWeight,
} from './PdfTemplateService';
import { CORE_VALUES } from '../../views/pages/Staff/PerformanceManagement/OneOnOne/Form/formConstants';
import { formatPolicyViolationText } from './FeedbackPdfService';

const ONE_ON_ONE_PDF_URL =
  'https://arb-systems-s3-public.s3.amazonaws.com/public/Performance/PerformanceManagement_OneOnOne.pdf';

const formatDateValue = (value?: string) => {
  if (!value) {
    return '';
  }

  const parsed = moment(value);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.format('MM/DD/YYYY');
};

const extractField = (data: any, candidates: string[]) => {
  for (const key of candidates) {
    const segments = key.split('.');
    let current = data;

    for (const segment of segments) {
      if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
        current = current[segment];
      } else {
        current = undefined;
        break;
      }
    }

    if (current !== undefined && current !== null) {
      if (typeof current === 'string') {
        const trimmed = current.trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }

      if (typeof current === 'number') {
        return current.toString();
      }

      if (current instanceof Date) {
        return moment(current).format('MM/DD/YYYY');
      }
    }
  }

  return '';
};

const sanitizeFileName = (text: string) => {
  if (!text) {
    return 'one-on-one';
  }

  return (
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 80) || 'one-on-one'
  );
};

interface AddTextOptions {
  y?: number;
  x?: number;
  textColor?: string;
  page?: number;
  fontSize?: number;
  fontWeight?: PdfTemplateFontWeight;
  maxWidth?: number;
  lineHeight?: number;
  spacingAfter?: number;
}

const buildOneOnOneLayout = (item: any, userLogged?: any, currentSchool?: any) => {
  const employeeName = extractField(item, ['name', 'employeeName', 'employee_name', 'employee.name']) || '';
  const position = extractField(item, ['position', 'employeePosition', 'jobTitle', 'job_title']) || '';
  const supervisor = extractField(item, ['supervisor', 'supervisor.name', 'supervisorName', 'supervisor_name']) || '';
  const dateOfIncident = formatDateValue(extractField(item, ['date_of_incident', 'dateOfIncident', 'date']));
  const department = extractField(item, ['department', 'department.name', 'departmentName']);
  const meetingGoal = extractField(item, ['meeting_goal', 'meetingGoal']);
  const coreValues = extractField(item, ['core_values', 'coreValues']);
  const descriptionOfOneOnOne = extractField(item, ['description_of_one_on_one', 'descriptionOfOneOnOne']);
  const createdAt = formatDateValue(extractField(item, ['created_at', 'createdAt']));
  const policyViolation = formatPolicyViolationText(item);

  const _programName = extractField(item, ['programName', 'program_name']);

  const layout: PdfTemplateLayoutItem[] = [];
  const addText = (text: string, options: AddTextOptions = {}) => {
    const { x, ...rest } = options;
    const textItem: PdfTemplateLayoutItem = {
      type: 'text',
      text,
      x: x ?? 54,
      ...rest,
    };
    layout.push(textItem);
  };

  addText(`${currentSchool?.label ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 16,
    textColor: '#fd9821',
    y: 711,
    x: 203,
  });

  const submittedByName = item?.submittedByUser
    ? `${item.submittedByUser.firstName ?? ''} ${item.submittedByUser.lastName ?? ''}`.trim()
    : '';

  addText(submittedByName, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 588,
    x: 160,
  });

  addText(`${employeeName}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 552,
    x: 110,
  });

  addText(`${dateOfIncident}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 552,
    x: 400,
  });

  addText(`${position}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 527,
    x: 120,
  });

  addText(`${department ?? ''}`, {
    fontWeight: 'regular',
    fontSize: 12,
    y: 527,
    x: 355,
  });

  const sequentialX = 80;
  const sequentialFontSize = 12;
  const spacingAfterTitle = 10;
  const spacingAfterContent = 22;

  addText('Policy Violation:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${policyViolation}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Description of One on One:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${descriptionOfOneOnOne}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Core Values:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${getCoreValueLabels(item.coreValues)}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  addText('Meeting Goal:', {
    fontWeight: 'bold',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterTitle,
  });
  addText(`${meetingGoal}`, {
    fontWeight: 'regular',
    fontSize: sequentialFontSize,
    x: sequentialX,
    spacingAfter: spacingAfterContent,
  });

  return {
    layout,
    employeeName,
    helpers: {
      addText,
      extractField,
      formatDateValue,
    },
    context: {
      item,
      userLogged,
      currentSchool,
      position,
      supervisor,
      dateOfIncident,
      department,
      meetingGoal,
      coreValues,
      descriptionOfOneOnOne,
      createdAt,
    },
  };
};

export interface OneOnOnePdfResult extends GeneratePdfTemplateResult {
  fileName: string;
}

export const generateOneOnOnePdf = async (
  item: any,
  userLogged?: any,
  currentSchool?: any,
): Promise<OneOnOnePdfResult> => {
  const { layout, employeeName } = buildOneOnOneLayout(item, userLogged, currentSchool);
  const result = await generatePdfFromTemplate({
    templatePath: ONE_ON_ONE_PDF_URL,
    layout,
    cursorStart: 730,
  });

  return {
    ...result,
    fileName: `${sanitizeFileName(employeeName)}-one-on-one.pdf`,
  };
};

const getCoreValueLabels = (values: string[]) => {
  if (!values || values.length === 0) return 'N/A';
  return values
    .map((valueId) => {
      const coreValue = CORE_VALUES.find((cv) => cv.id === valueId);
      return coreValue ? coreValue.label : valueId;
    })
    .join(', ');
};
